import { createContext, useContext, useEffect, useState } from "react";
import { User, onAuthStateChanged } from "firebase/auth";
import { auth, db } from "@/lib/firebase";
import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { UserProfile } from "@/types";

interface AuthContextType {
    user: User | null;
    profile: UserProfile | null;
    loading: boolean;
}

const AuthContext = createContext<AuthContextType>({ user: null, profile: null, loading: true });

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let unsubscribeProfile: (() => void) | undefined;

        const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);

            if (currentUser) {
                // Subscribe to profile changes
                const profileRef = doc(db, "users", currentUser.uid);
                unsubscribeProfile = onSnapshot(profileRef, (docSnap) => {
                    let profileData = docSnap.exists() ? docSnap.data() as UserProfile : null;
                    console.log("[AuthContext] Profile snapshot update:", {
                        exists: docSnap.exists(),
                        termsAccepted: profileData?.termsAccepted,
                        acceptedTermsVersion: (profileData as any)?.acceptedTermsVersion
                    });

                    // Superadmin failsafe: Ensure the designated email always has admin role in the UI
                    if (currentUser.email === "jebinjollyabraham@gmail.com") {
                        const needsUpdate = !profileData || profileData.role !== 'admin';

                        if (!profileData) {
                            profileData = {
                                uid: currentUser.uid,
                                email: currentUser.email!,
                                displayName: currentUser.displayName || "Super Admin",
                                role: "admin",
                                plan: "paid",
                                onboarded: true
                            };
                        } else if (profileData.role !== 'admin') {
                            profileData = { ...profileData, role: 'admin' };
                        }

                        // Auto-sync to Firestore if needed
                        if (needsUpdate) {
                            setDoc(profileRef, {
                                ...profileData,
                                updatedAt: serverTimestamp()
                            }, { merge: true }).catch(console.error);
                        }
                    }

                    setProfile(profileData);
                    setLoading(false);
                });
            } else {
                setProfile(null);
                if (unsubscribeProfile) unsubscribeProfile();
                setLoading(false);
            }
        });

        return () => {
            unsubscribeAuth();
            if (unsubscribeProfile) unsubscribeProfile();
        };
    }, []);

    return (
        <AuthContext.Provider value={{ user, profile, loading }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
